
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import Logo from '@/icons/Logo';

const PREMIUM_SETUP_LINK_ERROR = 'This setup link is invalid or has expired. Please request a new premium access email.';

const getSetupParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));

  return {
    email: searchParams.get('email') || hashParams.get('email') || '',
    tokenHash: searchParams.get('token_hash') || hashParams.get('token_hash') || '',
    type: searchParams.get('type') || hashParams.get('type') || '',
    errorCode: searchParams.get('error_code') || hashParams.get('error_code') || '',
    errorDescription: searchParams.get('error_description') || hashParams.get('error_description') || '',
  };
};

const PremiumSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    let isMounted = true;

    const applyAuthenticatedState = async (email: string) => {
      if (!isMounted) return;

      setAuthenticated(true);
      setUserEmail(email);

      if (email) {
        await supabase
          .from('premium_access_requests')
          .update({ signed_up_at: new Date().toISOString() })
          .eq('email', email.toLowerCase())
          .is('signed_up_at', null);
      }

      if (window.location.search || window.location.hash) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        await applyAuthenticatedState(session.user.email || '');
      }
    });

    const checkAuth = async () => {
      const params = getSetupParams();

      if (params.errorCode) {
        const decodedError = params.errorDescription
          ? decodeURIComponent(params.errorDescription.replace(/\+/g, ' '))
          : PREMIUM_SETUP_LINK_ERROR;
        if (isMounted) {
          setError(decodedError);
          setLoading(false);
        }
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await applyAuthenticatedState(session.user.email || params.email);
        return;
      }

      if (params.tokenHash) {
        const otpType: 'email' | 'recovery' = params.type === 'recovery' ? 'recovery' : 'email';
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          email: params.email || undefined,
          token_hash: params.tokenHash,
          type: otpType,
        });

        if (verifyError) {
          if (isMounted) {
            setError(PREMIUM_SETUP_LINK_ERROR);
            setLoading(false);
          }
          return;
        }

        await applyAuthenticatedState(data.user?.email || params.email);
        return;
      }

      if (isMounted) {
        setLoading(false);
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      setSuccess(true);
      setTimeout(() => navigate('/premium'), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-lg text-foreground">Verifying your access...</span>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2">
              <Logo className="h-10 w-10" />
            </div>
            <CardTitle className="text-2xl font-bold">Link Expired</CardTitle>
            <CardDescription>
              {error || 'This access link has expired or is invalid. Please contact your administrator to request a new link.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2">
              <Logo className="h-10 w-10" />
            </div>
            <CardTitle className="text-2xl font-bold">You're All Set!</CardTitle>
            <CardDescription>
              Your password has been set successfully. Redirecting to your dashboard...
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-2">
            <Logo className="h-10 w-10" />
          </div>
          <CardTitle className="text-2xl font-bold">
            Welcome to Maturity Compass
          </CardTitle>
          <CardDescription>
            Set up your password to complete your account setup.
            <br />
            <span className="text-xs text-muted-foreground">
              Signed in as {userEmail}
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSetPassword} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="setup-password">Password</Label>
              <div className="relative">
                <Input
                  id="setup-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={saving}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup-confirm">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="setup-confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={saving}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? 'Setting up...' : 'Set Password & Continue'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/premium')}
                className="text-sm text-muted-foreground hover:underline"
              >
                Skip for now
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PremiumSetup;
